import React, { useState, useEffect, useRef } from "react";
import {
  Box, Card, CardContent, Container, Typography, Table, TableBody, TableCell,
  TableContainer, TableRow, TableHead, Chip, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, IconButton, Tabs, Tab, Tooltip,
  Stack, Divider,
} from "@mui/material";
import { Icon } from "@iconify/react";
import {
  collection, onSnapshot, addDoc, serverTimestamp, getDocs, query, where, orderBy,
} from "firebase/firestore";
import { db } from "../database/db";
import Page from "../components/Page";
import Scrollbar from "../components/Scrollbar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

const SEC_LETTER_TEMPLATE = (symbol, floatPct, fivShares, marketShares, timestamp) => `
SECURITIES AND EXCHANGE COMMISSION
Division of Enforcement
100 F Street, NE
Washington, D.C. 20549

Date: ${timestamp}

RE: Potential Market Manipulation — ${symbol} — FIV Community Ownership Alert

Dear Sir or Madam,

We are writing on behalf of Fire Investing, a financial technology platform that enables 
retail investors to track their brokerage portfolios and engage with a community of 
like-minded investors. Fire Investing operates the FIV (Fire Investing Volume) Meter, a 
proprietary aggregation tool that compiles the total share count of a given security held 
collectively by members of our platform.

It has come to our attention that the FIV Meter for ${symbol} has reached ${floatPct.toFixed(2)}% 
of the reported public float. Specifically, Fire Investing community members collectively 
hold approximately ${fivShares.toLocaleString()} shares of ${symbol} against a reported 
market float of approximately ${marketShares.toLocaleString()} shares.

Our system has flagged this concentration as potentially significant under the provisions of 
Section 9(a)(2) and Section 10(b) of the Securities Exchange Act of 1934, and Rule 10b-5 
promulgated thereunder. When a disproportionate share of a security's float is concentrated 
in a single community, the resulting buy or sell pressure may not accurately reflect 
fundamental market conditions, which can constitute a form of coordinated price influence.

We wish to make clear that Fire Investing does not coordinate, encourage, or solicit 
investment activity in any specific security. Our platform provides portfolio tracking and 
community discussion tools only. However, given the magnitude of this aggregated position, 
we believe it is our responsibility to proactively disclose this data to the Commission so 
that appropriate review may be conducted.

Fire Investing respectfully requests that the Commission review the trading activity of 
${symbol} for the period surrounding this notification to determine whether any market 
manipulation or coordinated trading activity has occurred.

We are prepared to cooperate fully with any inquiry and can provide detailed user data 
pursuant to a valid legal request.

Respectfully submitted,

Fire Investing Administration
Email: mytag.llc@gmail.com
Platform: Fire Investing (Fintech)

—————————————————————————
[AUTOMATED FIV ALERT — Generated by Fire Investing Admin System]
Symbol: ${symbol}
FIV Shares: ${fivShares.toLocaleString()}
Market Float: ${marketShares.toLocaleString()}
Community Ownership: ${floatPct.toFixed(2)}%
Alert Timestamp: ${timestamp}
—————————————————————————
`.trim();

export default function FIVMonitor() {
  const [liveStocks, setLiveStocks] = useState([]);
  const [liveAlerts, setLiveAlerts] = useState([]);
  const [archive, setArchive] = useState([]);
  const [tab, setTab] = useState(0);
  const [letterDialog, setLetterDialog] = useState({ open: false, item: null, letter: "" });
  const [emailLoading, setEmailLoading] = useState(false);

  useEffect(() => {
    // Firebase collection: "squeeze"
    // Schema: one doc per ticker (doc id = ticker symbol e.g. "MMTLP")
    // Fields: { total_quantity: number, updated: timestamp }
    const unsub = onSnapshot(collection(db, "squeeze"), (snap) => {
      const all = snap.docs
        .filter((d) => /^[A-Z]{1,6}$/.test(d.id)) // only ticker-symbol docs
        .map((d) => {
          const data = d.data();
          const fivShares = parseInt(data.total_quantity, 10) || 0;
          const updatedTs = data.updated?.toDate?.()?.toLocaleDateString() || "";
          return {
            id: d.id,
            symbol: d.id,
            fivShares,
            marketShares: 0,
            squeeze: "N/A",
            updatedTs,
          };
        })
        .filter((s) => s.fivShares > 0)
        .sort((a, b) => b.fivShares - a.fivShares);

      setLiveStocks(all);
      // Flag tickers with 10k+ community shares as potential alerts
      setLiveAlerts(all.filter((s) => s.fivShares >= 10000));
    });

    loadArchive();
    return () => unsub();
  }, []);

  const loadArchive = async () => {
    const snap = await getDocs(
      query(collection(db, "LegalArchive"), orderBy("timestamp", "desc"))
    );
    setArchive(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const openLetter = (item) => {
    const ts = item.timestamp?.toDate?.()?.toLocaleString() || new Date().toLocaleString();
    const pct = parseFloat(item.squeeze || ((item.fivShares / item.marketShares) * 100).toFixed(2));
    const letter = SEC_LETTER_TEMPLATE(item.symbol, pct, item.fivShares, item.marketShares, ts);
    setLetterDialog({ open: true, item, letter });
  };

  const handleArchiveAndGenerate = async (stock) => {
    try {
      const pct = ((stock.fivShares / stock.marketShares) * 100).toFixed(2);
      await addDoc(collection(db, "LegalArchive"), {
        symbol: stock.symbol,
        fivShares: stock.fivShares,
        marketShares: stock.marketShares,
        squeeze: pct,
        timestamp: serverTimestamp(),
        reason: `FIV exceeded 75% — ${pct}% of float`,
      });
      toast.success(`${stock.symbol} archived to LegalArchive`);
      loadArchive();
      openLetter({ ...stock, squeeze: pct, timestamp: { toDate: () => new Date() } });
    } catch (err) {
      toast.error("Archive failed: " + err.message);
    }
  };

  const handleExportLetter = () => {
    const blob = new Blob([letterDialog.letter], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `SEC_Letter_${letterDialog.item?.symbol}_${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Letter exported");
  };

  const handleEmailLetter = async () => {
    setEmailLoading(true);
    try {
      // Store email request in Firestore (Firebase Cloud Function or EmailJS would send it)
      await addDoc(collection(db, "SECEmailQueue"), {
        to: "enforcement@sec.gov",
        from: "mytag.llc@gmail.com",
        subject: `FIV Alert — Potential Market Concentration: ${letterDialog.item?.symbol}`,
        body: letterDialog.letter,
        symbol: letterDialog.item?.symbol,
        queuedAt: serverTimestamp(),
        status: "queued",
      });
      toast.success(
        "Email queued! Set up Firebase Extension 'Trigger Email' or EmailJS to auto-send from mytag.llc@gmail.com"
      );
    } catch (err) {
      toast.error("Failed to queue email: " + err.message);
    }
    setEmailLoading(false);
  };

  const getSeverityColor = (pct) => {
    const p = parseFloat(pct);
    if (p >= 100) return "error";
    if (p >= 90) return "warning";
    return "info";
  };

  return (
    <Page title="FIV Monitor | Fire Investing Admin">
      <ToastContainer position="top-right" />
      <Container maxWidth="xl">
        <Box sx={{ pb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>FIV Monitor</Typography>
          <Typography variant="body2" color="text.secondary">
            Stocks where Fire Investing community owns ≥75% of the public float
          </Typography>
        </Box>

        {liveAlerts.length > 0 && (
          <Card sx={{ borderRadius: 2, mb: 3, bgcolor: "#FF4842", color: "#fff" }}>
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Icon icon="fa-solid:exclamation-triangle" width={20} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  LIVE ALERT: {liveAlerts.length} stock(s) currently at or above 75% FIV threshold
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label={`Live Alerts (${liveAlerts.length})`} />
          <Tab label={`SEC Archive (${archive.length})`} />
        </Tabs>

        {tab === 0 && (
          <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {["Symbol", "FIV Shares", "Market Float", "Community %", "Status", "Actions"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {liveAlerts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No stocks currently above 75% FIV threshold
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    liveAlerts.map((stock) => (
                      <TableRow key={stock.id} hover>
                        <TableCell><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{stock.symbol}</Typography></TableCell>
                        <TableCell>{(stock.fivShares || 0).toLocaleString()}</TableCell>
                        <TableCell>{(stock.marketShares || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={`${stock.squeeze}%`}
                            color={getSeverityColor(stock.squeeze)}
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label="ALERT" color="error" size="small" />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Button size="small" variant="contained" color="error"
                              onClick={() => handleArchiveAndGenerate(stock)}
                              startIcon={<Icon icon="fa-solid:file-contract" />}>
                              Archive & Generate SEC Letter
                            </Button>
                            <Button size="small" variant="outlined"
                              onClick={() => openLetter({ ...stock, timestamp: { toDate: () => new Date() } })}
                              startIcon={<Icon icon="eva:eye-fill" />}>
                              Preview Letter
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {tab === 1 && (
          <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {["Symbol", "FIV Shares", "Market Float", "Community %", "Timestamp", "Actions"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {archive.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No archived SEC events yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    archive.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{item.symbol}</Typography></TableCell>
                        <TableCell>{(item.fivShares || 0).toLocaleString()}</TableCell>
                        <TableCell>{(item.marketShares || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip label={`${item.squeeze}%`} color={getSeverityColor(item.squeeze)} sx={{ fontWeight: 700 }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {item.timestamp?.toDate?.()?.toLocaleString() || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined" startIcon={<Icon icon="fa-solid:file-contract" />}
                            onClick={() => openLetter(item)}>
                            View / Email Letter
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Container>

      {/* Letter Dialog */}
      <Dialog open={letterDialog.open} onClose={() => setLetterDialog({ open: false, item: null, letter: "" })}
        maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Icon icon="fa-solid:file-contract" />
            <span>SEC Letter — {letterDialog.item?.symbol}</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth multiline rows={24}
            value={letterDialog.letter}
            onChange={(e) => setLetterDialog((p) => ({ ...p, letter: e.target.value }))}
            inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLetterDialog({ open: false, item: null, letter: "" })}>Close</Button>
          <Button variant="outlined" startIcon={<Icon icon="eva:download-fill" />} onClick={handleExportLetter}>
            Export .txt
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Icon icon="eva:email-fill" />}
            onClick={handleEmailLetter}
            disabled={emailLoading}
          >
            {emailLoading ? "Queuing..." : "Email to SEC"}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
